import * as Sentry from '@sentry/browser';

/**
 * PWA Sentry entegrasyonu (#43).
 *
 * VITE_SENTRY_DSN yoksa Sentry devre dışı (local dev). main.ts'in EN BAŞINDA
 * initSentry() çağrılmalı — sonraki hatalar yakalanabilsin.
 *
 * KVKK: maskAllText + blockAllMedia + sendDefaultPii=false. Fiyat değerleri
 * kullanıcı dataı sayılır, Replay'de maskelenir.
 *
 * task-43-sentry-entegrasyonu.md §13-14
 */
const env = import.meta.env as Record<string, string | undefined>;

let enabled = false;

export function initSentry(): void {
    const dsn = env.VITE_SENTRY_DSN;
    if (!dsn) {
        console.info('[sentry] VITE_SENTRY_DSN yok — Sentry devre dışı');
        return;
    }

    Sentry.init({
        dsn,
        environment: import.meta.env.MODE,
        release: env.VITE_SENTRY_RELEASE,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true,
            }),
        ],
        tracesSampleRate: Number(env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.05),
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 1.0,
        sendDefaultPii: false,
        beforeSend(event) {
            // JWT/kart/parola alanlarını extra'dan temizle
            if (event.extra) {
                for (const key of Object.keys(event.extra)) {
                    if (/token|jwt|card|cvc|password/i.test(key)) {
                        event.extra[key] = '[FILTERED]';
                    }
                }
            }
            return event;
        },
        initialScope: {
            tags: { service: 'pwa' },
        },
    });

    enabled = true;
    console.info('[sentry] PWA initialized', { env: import.meta.env.MODE });
}

/** Cihaz eşleşince tenant/device tag'lerini set eder. */
export function setDeviceContext(opts: { tenantId?: string; deviceId?: string }): void {
    if (!enabled) return;
    Sentry.getCurrentScope().setTags({
        ...(opts.tenantId ? { tenant_id: opts.tenantId } : {}),
        ...(opts.deviceId ? { device_id: opts.deviceId } : {}),
    });
    if (opts.deviceId) {
        Sentry.setUser({ id: opts.deviceId });
    }
}

/** İz bırakma — bağlantı/SW/OTA olayları. */
export function breadcrumb(category: string, message: string, data?: Record<string, unknown>): void {
    if (!enabled) return;
    Sentry.addBreadcrumb({ category, message, level: 'info', data });
}

/** Hata yakalama — error-handlers.ts global handler'larından çağrılır. */
export function captureError(error: unknown, context?: Record<string, unknown>): void {
    if (!enabled) return;
    Sentry.captureException(error, context ? { extra: context } : undefined);
}

export { Sentry };

// Dev convenience — konsoldan window.testSentry()
if (import.meta.env.DEV) {
    (window as unknown as { testSentry: () => void }).testSentry = () => {
        throw new Error('Sentry PWA test — bu event Sentry UI\'da görünmeli');
    };
}
